/**
 * RemitSlice Selectors
 * 
 * Memoized selectors for accessing remit slice state.
 * Uses createSelector for performance optimization.
 */

import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import { RemitSliceState } from './state';

// Base selector to get the remit slice
const selectRemitSlice = (state: RootState): RemitSliceState => state.remit;

// =============================================================================
// CORE SELECTORS
// =============================================================================

/**
 * Select the current remit entity
 */
export const selectCurrentRemit = createSelector(
  selectRemitSlice,
  (slice) => slice.currentRemit
);

/**
 * Select the remit backup
 */
export const selectRemitBackup = createSelector(
  selectRemitSlice,
  (slice) => slice.remitBackup
);

/**
 * Select whether current remit exists
 */
export const selectHasCurrentRemit = createSelector(
  selectCurrentRemit,
  (remit) => remit !== null
);

/**
 * Select the remit ID (if exists)
 */
export const selectRemitId = createSelector(
  selectCurrentRemit,
  (remit) => remit?.id
);

/**
 * Select the trace number
 */
export const selectTraceNumber = createSelector(
  selectCurrentRemit,
  (remit) => remit?.traceNumber
);

// =============================================================================
// SELECTION SELECTORS
// =============================================================================

/**
 * Select the currently selected claim ID
 */
export const selectSelectedClaimId = createSelector(
  selectRemitSlice,
  (slice) => slice.selectedClaimId
);

/**
 * Select the currently selected service line ID
 */
export const selectSelectedServiceLineId = createSelector(
  selectRemitSlice,
  (slice) => slice.selectedServiceLineId
);

/**
 * Select the currently selected claim
 */
export const selectSelectedClaim = createSelector(
  [selectCurrentRemit, selectSelectedClaimId],
  (remit, claimId) => {
    if (!remit || !claimId) return null;
    return remit.claims.find((c) => c.id === claimId) ?? null;
  }
);

/**
 * Select the currently selected service line
 */
export const selectSelectedServiceLine = createSelector(
  [selectSelectedClaim, selectSelectedServiceLineId],
  (claim, serviceLineId) => {
    if (!claim || !serviceLineId) return null;
    return claim.serviceLines.find((sl) => sl.id === serviceLineId) ?? null;
  }
);

// =============================================================================
// EDITING STATE SELECTORS
// =============================================================================

/**
 * Select whether there are unsaved changes
 */
export const selectIsEdited = createSelector(
  selectRemitSlice,
  (slice) => slice.isEdited
);

/**
 * Select the list of edited field names
 */
export const selectEditedFields = createSelector(
  selectRemitSlice,
  (slice) => slice.editedFields
);

/**
 * Select whether a specific field has been edited
 */
export const selectIsFieldEdited = (fieldName: string) =>
  createSelector(
    selectEditedFields,
    (editedFields) => editedFields.includes(fieldName)
  );

// =============================================================================
// VALIDATION SELECTORS
// =============================================================================

/**
 * Select all validation errors
 */
export const selectValidationErrors = createSelector(
  selectRemitSlice,
  (slice) => slice.validationErrors
);

/**
 * Select whether there are any validation errors
 */
export const selectHasValidationErrors = createSelector(
  selectValidationErrors,
  (errors) => Object.keys(errors).length > 0
);

/**
 * Select validation error for a specific field
 */
export const selectFieldValidationError = (fieldName: string) =>
  createSelector(
    selectValidationErrors,
    (errors) => errors[fieldName]
  );

// =============================================================================
// LOADING STATE SELECTORS
// =============================================================================

/**
 * Select loading state
 */
export const selectIsLoading = createSelector(
  selectRemitSlice,
  (slice) => slice.isLoading
);

// =============================================================================
// REMIT DETAIL SELECTORS
// =============================================================================

/**
 * Select payer information
 */
export const selectPayer = createSelector(
  selectCurrentRemit,
  (remit) => remit?.payer
);

/**
 * Select payee information
 */
export const selectPayee = createSelector(
  selectCurrentRemit,
  (remit) => remit?.payee
);

/**
 * Select check/payment information
 */
export const selectCheck = createSelector(
  selectCurrentRemit,
  (remit) => remit?.check
);

/**
 * Select payment amount
 */
export const selectPaymentAmount = createSelector(
  selectCurrentRemit,
  (remit) => remit?.paymentAmount ?? 0
);

/**
 * Select all claims
 */
export const selectClaims = createSelector(
  selectCurrentRemit,
  (remit) => remit?.claims ?? []
);

/**
 * Select claim count
 */
export const selectClaimCount = createSelector(
  selectClaims,
  (claims) => claims.length
);

/**
 * Select provider adjustments
 */
export const selectProviderAdjustments = createSelector(
  selectCurrentRemit,
  (remit) => remit?.providerAdjustments ?? []
);

/**
 * Select validation errors from the remit
 */
export const selectRemitValidationErrors = createSelector(
  selectCurrentRemit,
  (remit) => remit?.validationErrors ?? []
);

/**
 * Select total paid amount across all claims
 */
export const selectTotalPaidAmount = createSelector(
  selectClaims,
  (claims) => claims.reduce((total, claim) => total + claim.totalPaidAmount, 0)
);

/**
 * Select total charged amount across all claims
 */
export const selectTotalChargedAmount = createSelector(
  selectClaims,
  (claims) => claims.reduce((total, claim) => total + claim.totalChargedAmount, 0)
);

/**
 * Select remittance date
 */
export const selectRemittanceDate = createSelector(
  selectCurrentRemit,
  (remit) => remit?.remittanceDate
);

/**
 * Select payment date
 */
export const selectPaymentDate = createSelector(
  selectCurrentRemit,
  (remit) => remit?.paymentDate
);
